//////////////////
// IMPORTS
//////////////////
// libraries
import * as React from "react";
import { connect } from "react-redux";
import * as ISchema from "@schema";
import { ApolloConsumer, ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { Environment } from "relay-runtime";
// styles
import * as s from "./ChromeNavContent.styles";
// other components
import { LogoHorizontal } from "@components/logo";
import { ChromeNavTabs } from "./ChromeNavTabs";
import { IProps as INavProps } from "../ChromeNav.d";
import { AutopilotStatusRenderer } from "@components/autopilot-status";
import { FreemiumStatusCallout } from "@components/freemium-status-callout";
import { SchedulingLinksSidebar } from "@clockwise/web-commons/src/ui/scheduling-links-sidebar";
import type { SidebarLink } from "@clockwise/web-commons/src/ui/scheduling-links-sidebar/scheduling-links-sidebar";
// util
import { getBulkExperiments } from "@util/experiment.util";
import { logger } from "@util/logger.util";
import { makeLinkName } from "@util/scheduler.util";
import { PostMessageManager } from "@util/post-message.util";
import copyToClipboard from "@src/util/copyToClipboard";

// state
import { IReduxState } from "@reducers/root.reducer";
import { setMenuDrawerOpen, setShareBestTimesLink } from "@actions/chrome-extension.actions";
import { setPricingDialogOpen } from "@actions/payments.actions";
import { setLinkName, setSchedulingLinks } from "@actions/settings.actions";

// material-ui
import {
  IPaymentStatusBadge,
  PaymentStatusBadgeTextEnum,
  PaymentStatusBadgeTypeEnum,
} from "@clockwise/web-commons/src/util/feature-grid.util";
import { IconButton, Tooltip } from "@material-ui/core";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import { Menu, ArrowDropDownCircle } from "@material-ui/icons";
import { track, TrackingEvents } from "@util/analytics.util";

//////////////////
// TYPES
//////////////////
interface IContainer {
  showMenu?: boolean;
  showTabs?: boolean;
  showCollapse?: boolean;
  showLogo?: boolean;
  environment: Environment;
  org?: INavProps["org"];
}

interface IProps extends IContainer, WithStyles<typeof s.styles> {
  autopilotPaused: boolean;
  fastCrawlFinished: boolean;
  isWebExtension: boolean;
  linkName: string;
  paymentStatusBadge: IPaymentStatusBadge | null;
  schedulingLinks: ISchema.ISchedulingLink[];
  setLinkName: typeof setLinkName;
  setMenuDrawerOpen: typeof setMenuDrawerOpen;
  setPricingDialogOpen: typeof setPricingDialogOpen;
  setSchedulingLinkCopyBestTimes: typeof setShareBestTimesLink;
  setSchedulingLinks: typeof setSchedulingLinks;
  showHeatmap: boolean;
  openShareLinkModal: IReduxState["webExtension"]["openShareLinkModal"];
}

//////////////////
// COMPONENT
//////////////////
class ChromeNavHomeCmpt extends React.Component<IProps> {
  constructor(props: IProps) {
    super(props);
  }

  // ~-~-~-~-~-~-~-
  // Event Handlers
  // ~-~-~-~-~-~-~-
  private onClickMenu = () => {
    this.props.setMenuDrawerOpen(true);
  };

  private onClickClose = () => {
    PostMessageManager.closeSidebar(true);
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private maybeRenderAutopilotStatusRenderer = () => {
    const { environment, org, autopilotPaused } = this.props;
    const orgTimeZone = (org && org.orgMeetingHours?.workingHours.timeZone) || undefined;

    if (!org || !orgTimeZone) {
      return "";
    }

    if (autopilotPaused) {
      return (
        <div style={{ margin: "-5px 10px 0" }}>
          <FreemiumStatusCallout org={org} />
        </div>
      );
    }

    return (
      <div style={{ margin: "-16px 10px 0" }}>
        <AutopilotStatusRenderer
          environment={environment}
          orgRelayId={org.id}
          orgTimeZone={orgTimeZone}
          fastCrawlFinished={this.props.fastCrawlFinished}
          isActionable
        />
      </div>
    );
  };

  private onBadgeClick = () => {
    track(TrackingEvents.PAYWALLS.CHROME_EXTENSION_PLAN_BADGE_CLICKED);
    this.props.setPricingDialogOpen(true);
  };

  private maybeRenderPlanBadge = () => {
    const { classes, paymentStatusBadge } = this.props;

    if (!paymentStatusBadge) {
      return null;
    }

    const isFreeOrProLayout =
      paymentStatusBadge.text == PaymentStatusBadgeTextEnum.Free ||
      paymentStatusBadge.text == PaymentStatusBadgeTextEnum.Pro;
    const isEnterprise = paymentStatusBadge.text == PaymentStatusBadgeTextEnum.Enterprise;
    return (
      <div
        cw-id="home-plan-badge"
        className={
          paymentStatusBadge.type === PaymentStatusBadgeTypeEnum.Blue
            ? classes.badgeBlue
            : classes.badgeGradient
        }
        onClick={this.onBadgeClick}
        style={{
          marginLeft: isFreeOrProLayout ? "0px" : "7px",
          fontSize: isEnterprise ? "10px" : "11px",
        }}
      >
        {paymentStatusBadge.text}
      </div>
    );
  };

  private onBeforeOpenShareModal = async (
    client: ApolloClient<NormalizedCacheObject>,
    link: SidebarLink,
  ): Promise<SidebarLink> => {
    if (!link.linkName) {
      const { schedulingLinks } = await makeLinkName(
        client,
        this.props.setSchedulingLinks,
        this.props.setLinkName,
      );
      return schedulingLinks[0];
    }
    return link;
  };

  public render() {
    const {
      showCollapse,
      showMenu,
      showTabs,
      showLogo,
      org,
      schedulingLinks,
      linkName,
      paymentStatusBadge,
      openShareLinkModal,
    } = this.props;
    const activeSchedulingLinks = schedulingLinks.filter((link) => link.meetingSettings.isActive);

    const experiments = getBulkExperiments(org || null);

    let isFreeOrProLayout = false;
    if (paymentStatusBadge != null) {
      isFreeOrProLayout =
        paymentStatusBadge.text == PaymentStatusBadgeTextEnum.Free ||
        paymentStatusBadge.text == PaymentStatusBadgeTextEnum.Pro;
    }

    return (
      <ApolloConsumer>
        {(client: ApolloClient<NormalizedCacheObject>) => {
          const getOrMakeLinkUrl = async (
            linkUrl: string,
            meetingSettingsId: string | null,
          ): Promise<{ linkUrl: string | null; meetingSettingsId?: string | null }> => {
            if (linkUrl) {
              return { linkUrl, meetingSettingsId };
            } else {
              try {
                const result = await makeLinkName(
                  client,
                  this.props.setSchedulingLinks,
                  this.props.setLinkName,
                );
                return {
                  linkUrl: result.schedulingLinks[0].linkUrl,
                  meetingSettingsId: result.schedulingLinks[0].meetingSettings.id,
                };
              } catch (e) {
                logger.error("There was an error trying to create an initial link", e);
                return { linkUrl: null, meetingSettingsId: null };
              }
            }
          };

          return (
            <div cw-id="chrome-nav-home">
              {showLogo ? (
                <div style={s.containerHome}>
                  <Tooltip title="Menu">
                    <IconButton
                      style={{
                        visibility: showMenu ? "visible" : "hidden",
                        padding: isFreeOrProLayout ? "12px" : "9px",
                      }}
                      onClick={() => this.onClickMenu()}
                    >
                      <Menu htmlColor={s.logo.fill} />
                    </IconButton>
                  </Tooltip>
                  <LogoHorizontal {...s.logo} />
                  {this.maybeRenderPlanBadge()}
                  <Tooltip title="Close">
                    <IconButton
                      style={{
                        ...s.navCollapseButton,
                        visibility: showCollapse ? "visible" : "hidden",
                      }}
                      onClick={() => this.onClickClose()}
                    >
                      <ArrowDropDownCircle
                        id="cwClose"
                        className={this.props.classes.navCollapse}
                      />
                    </IconButton>
                  </Tooltip>
                </div>
              ) : null}
              {showTabs && (
                <>
                  {this.maybeRenderAutopilotStatusRenderer()}
                  {org && org?.id && experiments.LinkScheduler && (
                    <div style={{ margin: "11px 11px" }} cw-id="scheduling-link-card">
                      <SchedulingLinksSidebar
                        links={linkName ? activeSchedulingLinks : null}
                        copyTimesLinkEnabled={experiments.LinkSchedulerCopyTimes}
                        openShareLinkModal={openShareLinkModal}
                        onOpen={async (rawLinkUrl, rawMeetingSettingsId) => {
                          const { linkUrl, meetingSettingsId } = await getOrMakeLinkUrl(
                            rawLinkUrl,
                            rawMeetingSettingsId,
                          );
                          if (!linkUrl) {
                            return;
                          }
                          track(TrackingEvents.LINKS.SIDEPANEL.VIEW_PREVIEW, {
                            meetingSettingsId,
                          });
                          window.open(linkUrl);
                        }}
                        onCopy={async (rawLinkUrl, rawMeetingSettingsId) => {
                          const { linkUrl, meetingSettingsId } = await getOrMakeLinkUrl(
                            rawLinkUrl,
                            rawMeetingSettingsId,
                          );
                          if (!linkUrl) {
                            logger.error(
                              "Failed to copy link url to clipboard upon creating new linkName",
                            );
                            return false;
                          }

                          copyToClipboard(linkUrl);

                          track(TrackingEvents.LINKS.SIDEPANEL.COPY_LINK, {
                            meetingSettingsId,
                            surface: "sidebar",
                          });
                          return true;
                        }}
                        onOpenShareModal={async (link: SidebarLink) => {
                          const validLink = await this.onBeforeOpenShareModal(client, link);
                          this.props.setSchedulingLinkCopyBestTimes(validLink);
                        }}
                      />
                    </div>
                  )}
                  <ChromeNavTabs />
                </>
              )}
            </div>
          );
        }}
      </ApolloConsumer>
    );
  }
}

/////////////////
// WITH STYLES //
/////////////////

const ChromeNavHomeStyled = withStyles(s.styles)(ChromeNavHomeCmpt);

//////////////////
// REDUX
//////////////////

function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    fastCrawlFinished: state.onboarding.fastCrawlFinished,
    showHeatmap: state.webExtension.showHeatmap,
    paymentStatusBadge: state.featureGrid.mixedFreemiumOrgUIState.paymentStatusBadge,
    autopilotPaused: state.featureGrid.mixedFreemiumOrgUIState.autopilotPaused,
    schedulingLinks: state.settings.schedulingLinks,
    linkName: state.settings.linkName,
    isWebExtension: state.webExtension.isWebExtension,
    openShareLinkModal: state.webExtension.openShareLinkModal,
  };
}

export const ChromeNavHome = connect(mapStateToProps, {
  setLinkName,
  setMenuDrawerOpen,
  setPricingDialogOpen,
  setSchedulingLinkCopyBestTimes: setShareBestTimesLink,
  setSchedulingLinks,
})(ChromeNavHomeStyled);
